import {
  MESSAGE_CHUNK,
  MESSAGE_ATTRIBUTE,
  MESSAGE_CONTENT_RECEIVED,
  LOADING_TEXT,
  LOADING_EMOJI,
} from './constants';

/**
 * An instance of JsonAssembler represents a message which can be updated using the packets
 * received on the websocket.
 *
 * The AiSuggestion component renders a chat of messages. This chat is built using packets
 * incrementally. We receive a stream of JSON packets from the server.
 *
 * A packet has the following structure:
 * {
 *   type: MESSAGE_CHUNK is appended to the content of the message, MESSAGE_ATTRIBUTE is
 *         appended to the given attribute name.
 *   chunk: { // only applicable for MESSAGE_CHUNK
 *     text: "text to append to message content"
 *   }
 *   attribute: { // only applicable to MESSAGE_ATTRIBUTE
 *     key: "attribute name",
 *     value: "value to append to this key in the json"
 *   }
 * }
 *
 */

export default class JsonAssembler {
  constructor() {
    this.contentReceived = false;
    this.data = {
      content: '',
    };
  }

  isContentReceived = () => {
    return this.contentReceived;
  };

  processMessage = msg => {
    if (!msg) return;
    const { type, chunk, attribute } = msg;
    if (type === MESSAGE_CHUNK) {
      this.data.content += chunk?.text;
    } else if (type === MESSAGE_ATTRIBUTE) {
      if (!attribute?.key) return;
      if (!this.data[attribute.key]) this.data[attribute.key] = '';
      this.data[attribute.key] += attribute.value || '';
    } else if (type === MESSAGE_CONTENT_RECEIVED) {
      this.contentReceived = true;
    }
  };
}

/**
 * This function creates a static chat from a dynamic chat. The dynamic chat
 * contains JsonAssemblers which are constantly updated using the packets received from
 * the server.
 */

export const renderChat = (chat = []) => {
  const renderedChat = [];
  chat.forEach(request => {
    const { query, assemblers } = request;
    if (!query || !assemblers) return;

    const userMessage = {
      type: 'user',
      originalMessage: query,
      originalSentimentEmoji: LOADING_EMOJI,
      originalSentimentDescription: LOADING_TEXT,
    };
    renderedChat.push(userMessage);
    let allLoaded = true;
    for (let i = 0; i < assemblers.length; i++) {
      const assembler = assemblers[i];
      const data = assembler.data; // eslint-disable-line prefer-destructuring

      if (userMessage.originalSentimentEmoji === LOADING_EMOJI) {
        userMessage.originalSentimentEmoji =
          data.originalSentimentEmoji?.trim() ||
          userMessage.originalSentimentEmoji;
      }
      if (userMessage.originalSentimentDescription === LOADING_TEXT) {
        userMessage.originalSentimentDescription =
          data.originalSentimentDescription?.trim() ||
          userMessage.originalSentimentDescription;
      }

      renderedChat.push({
        type: 'bot',
        content: {
          content: data.content.trim(),
          modifiedSentimentDescription:
            data.modifiedSentimentDescription?.trim() || LOADING_TEXT,
          modifiedSentimentEmoji:
            data.modifiedSentimentEmoji?.trim() || LOADING_EMOJI,
        },
        suggestionLoaded: assembler.isContentReceived(),
      });
      if (!assembler.isContentReceived()) allLoaded = false;
    }
    if (!allLoaded) {
      for (let i = 0; i < assemblers.length; i++) {
        renderedChat[renderedChat.length - 1 - i].suggestionLoaded = false;
      }
    }
  });
  return renderedChat;
};
